import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { COMM_TYPES, firstContact, lastUpdate, uid, type Lead, type LeadComm } from './leadDomain';

interface Props {
  open: boolean;
  lead: Lead | null;
  people: string[];
  onClose: () => void;
  onAdd: (leadId: string, comm: LeadComm) => void;
}

const today = () => new Date().toISOString().slice(0, 10);

export function CommLogModal({ open, lead, people, onClose, onAdd }: Props) {
  const [date, setDate] = useState(today());
  const [type, setType] = useState<LeadComm['type']>(COMM_TYPES[0]);
  const [by, setBy] = useState('');
  const [note, setNote] = useState('');

  if (!lead) return null;
  const comms = [...(lead.comms ?? [])].sort((a, b) => b.date.localeCompare(a.date));

  function add() {
    onAdd(lead!.id, { id: uid(), date, type, by, note: note.trim() });
    setNote('');
  }

  return (
    <Modal open={open} onClose={onClose} title={`${lead.customer} — Communication log`} size="lg"
      footer={<Button variant="glass" onClick={onClose}>Close</Button>}
    >
      <div className="mb-3 flex flex-wrap gap-4 text-xs text-[var(--text-muted)]">
        <span>First contact: <strong className="text-[var(--text-primary)]">{firstContact(lead)}</strong></span>
        <span>Last update: <strong className="text-[var(--text-primary)]">{lastUpdate(lead)}</strong></span>
        <span>Logs: <strong className="text-[var(--text-primary)]">{comms.length}</strong></span>
      </div>

      <div className="mb-4 max-h-56 overflow-y-auto rounded-lg border border-[var(--border-color)]">
        {comms.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-[var(--text-muted)]">No entries yet. Log the first interaction below.</div>
        ) : (
          comms.map((c, i) => (
            <div key={c.id} className="px-4 py-2.5" style={{ borderBottom: i < comms.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
              <div className="mb-1 flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
                <Badge tone="info">{c.type}</Badge>
                <span>{c.date}{c.by ? ` · ${c.by}` : ''}</span>
              </div>
              {c.note && <div className="text-sm text-[var(--text-secondary)]">{c.note}</div>}
            </div>
          ))
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FormGroup label="Date">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </FormGroup>
        <FormGroup label="Type">
          <Select value={type} onChange={(e) => setType(e.target.value as LeadComm['type'])}>
            {COMM_TYPES.map((t) => <option key={t}>{t}</option>)}
          </Select>
        </FormGroup>
        <FormGroup label="Logged by" className="col-span-2">
          <Select value={by} onChange={(e) => setBy(e.target.value)}>
            <option value="">—</option>
            {people.map((p) => <option key={p}>{p}</option>)}
          </Select>
        </FormGroup>
        <FormGroup label="Note" className="col-span-2">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short summary of the call or interaction" />
        </FormGroup>
      </div>
      <div className="mt-3 flex justify-end">
        <Button variant="primary" size="sm" onClick={add}>Add entry</Button>
      </div>
    </Modal>
  );
}
